import type { ISODate } from "./types";

const DAY = 86_400_000;

export function toDay(d: ISODate): number {
  const [y, m, dd] = d.split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, dd) / DAY);
}

export function fromDay(n: number): ISODate {
  return new Date(n * DAY).toISOString().slice(0, 10);
}

export function addDays(d: ISODate, n: number): ISODate {
  return fromDay(toDay(d) + n);
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isISODate(s: unknown): s is ISODate {
  if (typeof s !== "string" || !ISO_RE.test(s)) return false;
  return fromDay(toDay(s)) === s;
}

/** Normalise user or Excel input to an ISO day. Accepts Date, Excel serials, ISO and d-MMM-yy strings. Time of day is dropped. */
export function parseDate(v: unknown): ISODate | null {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return null;
    return v.toISOString().slice(0, 10);
  }
  if (typeof v === "number" && isFinite(v)) {
    // Excel serial (1900 system): day 25569 = 1970-01-01.
    return fromDay(Math.floor(v) - 25569);
  }
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return null;
    const iso = s.slice(0, 10);
    if (isISODate(iso)) return iso;
    const m = s.match(/^(\d{1,2})[-\s/]([A-Za-z]{3})[A-Za-z]*[-\s/](\d{2,4})$/);
    if (m) {
      const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
      const mi = months.indexOf(m[2].toLowerCase());
      if (mi >= 0) {
        let y = Number(m[3]);
        if (y < 100) y += 2000;
        const out = `${y}-${String(mi + 1).padStart(2, "0")}-${m[1].padStart(2, "0")}`;
        if (isISODate(out)) return out;
      }
    }
    const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (us) {
      const out = `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
      if (isISODate(out)) return out;
    }
  }
  return null;
}
